import { ObjectUtils } from '@utils/helpers/index';

/**
 * Calculates and returns the cross sum (digit sum) of a number.
 * @param number the number whose cross sum is to be calculated
 * @returns the cross sum of the input number, 0 if the input number is NOT a
 * **valid positive integer**, because the cross sum mathematically is only
 * defined for natural numbers
 */
function crossSum( number: number ): number {
	if ( !numbersAreIntegers( number ) || number < 0 ) { return 0; }
	if ( number < 10 ) { return number; }

	const numbers: number[] =
		Array.from( `${ number }` )
		.map( ( digit: string ) => Number.parseInt( digit, 10 ) );

	const sum: number = numbers.reduce( ( accumulator: number, currentValue: number ) => accumulator + currentValue, 0 );

	return sum;
}

/**
 * Checks whether a number value is `NaN` (e.g. resulting from an arithmetic
 * operation involving a non-numeric value) or `Infinity` (e.g. resulting
 * from a division by 0) or negative `Infinity` (e.g. resulting from trying to
 * get Base-10 logarithm of 0).
 * @param number The number value to be checked
 * @returns
 * * `true` if the value is either `NaN` or `Infinity`
 * * `false` for any other value
 */
function isNanOrInfinity( number: number ): boolean {
	return [ NaN, Infinity, -Infinity ].includes( number );
}


/**
 * Checks a single number or sequence of numbers, and returns `false` as soon as
 * one of these numbers is not an integer, or if there are no numbers at all.
 * The numbers have to be passed in as a parameter list (or as an array or plain
 * object, using spread syntax).
 */
function numbersAreIntegers( ...numbers: number[] ): boolean {
	return numbers.length !== 0 &&
				 !numbers?.some( ( number: number ) => !Number.isInteger( number ) );
}


/**
 * Checks if a given integer value is within a defined range.
 * @param number the integer value to check
 * @param min the integer value defining the lower bound of the range
 * @param max the integer value defining the upper bound of the range
 * @param options Some specific options for the fine-tuning of the behavior of the function
 * @param options.checkMin flag for toggling check against `min` value, default is `true`
 * @param options.checkMax flag for toggling check against `max` value, default is `true`
 * @param options.minMaxIsIncluded flag for toggling inclusion of boundary values into
 * the range (i.e. if set to `true`, the check will pass if `number` equals one
 * of the range boundary values), default is `true`
 * @return boolean result of the defined check configuration, always `false` if
 * any of the numeric inputs is not an integer or if `min` value is greater than
 * `max` value (empty/invalid range).
 */
function numberIsInRange(
	number: number,
	min: number,
	max: number,
	options?: {
		checkMin?: boolean,
		checkMax?: boolean,
		minMaxIsIncluded?: boolean
	}
): boolean {
	const {
		checkMin = true,
		checkMax = true,
		minMaxIsIncluded = true,
	} = options ?? {};

	// check if all relevant number inputs are integers
	if ( !numbersAreIntegers( number ) ) {
		return false;
	}
	if ( checkMin && !numbersAreIntegers( min ) ) {
		return false;
	}
	if ( checkMax && !numbersAreIntegers( max ) ) {
		return false;
	}

	// is in min range?
	if ( checkMin ) {
		if ( !minMaxIsIncluded ) { min++; }

		if ( number < min ) {
			return false;
		}
	}

	// is in max range?
	if ( checkMax ) {
		if ( !minMaxIsIncluded ) { max--; }

		if ( number > max ) {
			return false;
		}
	}

	return true;
}


function numberIsInMinRange( number: number, min: number, minIsIncluded: boolean = true ): boolean {
	return numberIsInRange( number, min, null, { checkMax: false, minMaxIsIncluded: minIsIncluded } );
}


function numberIsInMaxRange( number: number, max: number, maxIsIncluded: boolean = true ): boolean {
	return numberIsInRange( number, null, max, { checkMin: false, minMaxIsIncluded: maxIsIncluded } );
}


/**
 * Rounds a floating point number to a specified number of decimals and returns
 * this rounded number.<br>
 * If the value of the floating point number after rounding is equivalent to an
 * integer, the number returned will be that integer.
 * For example, if `n` is `8.76543e-13`, and `decimals` is `7`, the returned
 * number will be `0`.
 * @param n The floating-point number to be rounded
 * @param decimals The number of decimals to be present in the return value.
 *                 Default value is 0.
 */
function roundFloat( n: number, decimals?: number ): number {
	if ( ObjectUtils.isNullish( n ) || isNanOrInfinity( decimals ) ) { return n; }

	const numericStringWithFixedDecimals = n.toFixed( decimals ?? 0 );
	return +numericStringWithFixedDecimals;
}


/**
 * Checks if the integer value of the input `number` is within the defined
 * range, and, if not, sets it to an integer value within this range and returns
 * this value.
 * @param number the number to check
 * @param min the integer value defining the lower bound of the range
 * @param max the integer value defining the upper bound of the range
 * @returns the unchanged input `number` value if
 * * the input `number` value is an integer and is within the defined range,
 * * or `min` is greater than `max` (empty/invalid range),
 * * or no valid `min` AND `max` values were passed in.<br>
 * Otherwise the nearest of the two boundary values (always the lower boundary
 * value if the input `number` is not an integer).
 */
function setNumberValueInsideRange( number: number, min?: number, max?: number ): number {

	if ( !numberIsInMinRange( number, min ) ) {
		number = numbersAreIntegers( min, max ) && min <= max ? min : number;
	}

	if ( !numberIsInMaxRange( number, max ) ) {
		number = numbersAreIntegers( min, max ) && max >= min ? max : number;
	}

	return number;
}


export {
	crossSum,
	isNanOrInfinity,
	numbersAreIntegers,
	numberIsInRange,
	numberIsInMaxRange,
	numberIsInMinRange,
	roundFloat,
	setNumberValueInsideRange
};
